import { useShopPageProps } from "#app/layouts/ShopLayout";
import { Box, Button, Card, CardContent, Grid, TextField, Typography, Avatar, Chip, Rating, Divider, Pagination, TextareaAutosize, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, Select, MenuItem, Link, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import { useEffect, useState } from "react";
import { useNickFetch } from "#app/hooks/useNickFetch";
import { sleep } from "#app/utils";
import { useNotice } from "#app/hooks/useNotice";
import LoadingButton from "@mui/lab/LoadingButton";
import { PostStatus, ShopSync, SyncStatus } from "#app/models";
import SyncStatusBar from "#app/components/SyncStatusBar";

/**
 * 投稿ステータス.
 */
var getStatusLabel = ( post_status: number ): string => {
	// return "test:" + post_status;
	const statusMap = {
		0: "処理待ち",
		1: "下書き",
		5: "生成済み",
		10: "投稿待ち",
		12: "投稿中",
		14: "投稿済み",
		16: "投稿エラー",
		20: "削除待ち",
		22: "削除中",
		24: "削除済み",
		26: "削除エラー",
		99: "無視"
	};
	return statusMap[post_status as keyof typeof statusMap] || "";
}
var getStatusColor = ( post_status: number ): string => {
	// return "test:" + post_status;
	const statusMap = {
		0: "default",
		1: "warning",
		5: "info",
		10: "warning",
		12: "warning",
		14: "success",
		16: "error",
		20: "warning",
		22: "warning",
		24: "success",
		26: "error",
		99: "default"
	};
	return statusMap[post_status as keyof typeof statusMap] || "";
}
var getOriginalReplyComment = ( review: Review ): string => {
	const json = JSON.parse(review.original_json);
	return json?.reviewReply?.comment;
}
type Review = {
	id: number;
	shop_id: number;
	source_platform: number;
	error_message: string;
	original_json: string;
	platform_review_id: string;
	post_dt: string | null;
	edit_dt: string;
	regist_dt: string;
	create_dt: string;
	star_rating: number;
	reviewer_name: string;
	review_comment: string;
	reply_body: string;
	reply_dt: string | null;
	post_status: number;
	reviewer_photo_url: string;
}

type ReviewListResponse = {
	reviews: Review[];
	shop_sync: ShopSync;
	total_count: number;
	total_pages: number;
	current_page: number;
	per_page: number;
};

export default function ShopReviews() {
	const { shop, reloadShop } = useShopPageProps();
	const [loading, setLoading] = useState<boolean>(false);
	const { nickFetch } = useNickFetch();
	const { addNotice } = useNotice();
	const [reviews, setReviews] = useState<Review[]>();
	const [shop_sync, setShopSync] = useState<ShopSync>(new ShopSync());
	
	// ページャー関連の状態
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalCount, setTotalCount] = useState<number>(0);

	// 絞り込み・並び替え関連の状態
	const [filterStatus, setFilterStatus] = useState<string>('all'); // 返信ステータス
	const [filterRating, setFilterRating] = useState<string>('all'); // 評価
	const [filterHasComment, setFilterHasComment] = useState<string>('all'); // コメントがあるか
	const [sortBy, setSortBy] = useState<string>('create_dt'); // 並び替え項目
	const [sortOrder, setSortOrder] = useState<string>('desc'); // 並び替え順序

	// 返信関連の状態
	const [replyTexts, setReplyTexts] = useState<{ [key: number]: string }>({});
	const [replyLoading, setReplyLoading] = useState<{ [key: number]: boolean }>({});

	// 自動生成ボタンのローディング状態
	const [generateLoading, setGenerateLoading] = useState<{ [key: number]: boolean }>({});

	const [showSettings, setShowSettings] = useState(false);
	const [selectedReview, setSelectedReview] = useState<Review | null>(null);
	const [detailDialogOpen, setDetailDialogOpen] = useState(false);
	const [simulatorOpen, setSimulatorOpen] = useState(false);
	const [simulatorData, setSimulatorData] = useState({
		sampleComment: "とてもよくしてもいました！また来たいと思います。",
		sampleRating: 5,
		sampleUserName: "山田太郎",
		prompt: "",
		generatedReply: ""
	});

	// サンプルコメントテンプレート
	const sampleCommentTemplates = [
		{
			label: "サンプル",
			comment: "とてもよくしてもいました！また来たいと思います。",
			rating: 5,
			userName: "山田太郎"
		},
		{
			label: "高評価（5星）",
			comment: "カットがとても上手で、スタイルも可愛く仕上がりました！スタッフの方々の対応も丁寧で、とても良い体験ができました。また来たいと思います。",
			rating: 5,
			userName: "田中花子"
		},
		{
			label: "中評価（3星）",
			comment: "カットは良かったですが、少し待ち時間が長かったです。技術は良いので、また機会があれば来てみたいと思います。",
			rating: 3,
			userName: "佐藤美咲"
		},
		{
			label: "低評価（1星）",
			comment: "カットが下手だったし、スタッフの対応も悪かったです。もう二度と来ません。",
			rating: 1,
			userName: "山田恵子"
		},
		{
			label: "評価のみ",
			comment: "",
			rating: 3,
			userName: "小林 さくら"
		},
		{
			label: "具体的な要望",
			comment: "カットは良かったですが、もう少し長めに残してほしかったです。また、予約が取りやすいと良いですね。",
			rating: 4,
			userName: "鈴木愛"
		},
		{
			label: "初回来店",
			comment: "初めて来ましたが、とても良いサロンでした！雰囲気も良くて、カットも上手でした。また来たいと思います。",
			rating: 5,
			userName: "高橋美香"
		},
		{
			label: "アイラッシュ",
			comment: "アイラッシュの仕上がりがとても綺麗で、自然な仕上がりに満足しています。スタッフの方も丁寧で、また来たいと思います。",
			rating: 5,
			userName: "小林麻衣"
		},
		{
			label: "ネイル",
			comment: "ネイルのデザインが可愛くて、仕上がりも綺麗でした。技術も良くて、また来たいと思います。",
			rating: 5,
			userName: "渡辺彩"
		},
		{
			label: "美容室（要望）",
			comment: "カットは良かったですが、もう少し短めにしてもらいたかったです。技術は良いので、次回は希望を伝えたいと思います。",
			rating: 4,
			userName: "中村由美"
		}
	];
	const [simulatorLoading, setSimulatorLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [syncSettings, setSyncSettings] = useState({
		enabled: 0,
		post_status: 0,
		bad_review_star: 2,
		bad_review_notify: 0,
		rate_only_reply: 0,
		start_date: "",
		prompt: ""
	});

	const refleshReviews = async (page: number = 1) => {
		setLoading(true);
		
		const params: any = {
			shop_id: shop.id,
			page: page,
			filter_status: filterStatus,
			filter_rating: filterRating,
			filter_has_comment: filterHasComment,
			sort_by: sortBy,
			sort_order: sortOrder
		};
		
		const res = await nickFetch("/reviews/get_reviews/", params);
		const data = res.data as ReviewListResponse;
		if ( !res.error ) {
			if ( data.reviews ) { setReviews(data.reviews); }
			if ( data.shop_sync ) { 
				setShopSync(new ShopSync(data.shop_sync));
				// 設定状態も同期
				setSyncSettings({
					enabled: Number(data.shop_sync.enabled),
					post_status: Number(data.shop_sync.post_status),
					bad_review_star: Number(data.shop_sync.bad_review_star),
					bad_review_notify: Number(data.shop_sync.bad_review_notify),
					rate_only_reply: Number(data.shop_sync.rate_only_reply),
					start_date: data.shop_sync.start_date || "",
					prompt: data.shop_sync.prompt || ""
				});
				if ( !data.shop_sync.id ) {
					setShowSettings(true);
				}
			}
			// ページャー情報を更新
			if (data.total_count !== undefined) setTotalCount(data.total_count);
			if (data.total_pages !== undefined) setTotalPages(data.total_pages);
			if (data.current_page !== undefined) setCurrentPage(data.current_page);
		}
		setLoading(false);
	}

	useEffect(() => {
		(async function () {
			await refleshReviews();
		})();
	}, [filterStatus, filterRating, filterHasComment, sortBy, sortOrder]);

	// ページ変更時の処理
	const handlePageChange = async (event: React.ChangeEvent<unknown>, value: number) => {
		await refleshReviews(value);
	};

	// 返信テキストの更新
	const handleReplyTextChange = (reviewId: number, text: string) => {
		setReplyTexts(prev => ({
			...prev,
			[reviewId]: text
		}));
	};

	// 下書き保存
	const handleSaveDraft = async (review: Review) => {
		const replyText = replyTexts[review.id]?.trim();

		setReplyLoading(prev => ({
			...prev,
			[review.id]: true
		}));

		try {
			const res = await nickFetch("/reviews/save_draft_reply/", {
				review_id: review.id,
				reply_comment: replyText
			});

			if (!res.error) {
				setSelectedReview( res.data.review as Review );
				await refleshReviews(currentPage);
				addNotice("下書きを保存しました", "success", "bottom");
			} else {
				addNotice('下書きの保存に失敗しました: ' + res.error, "error", "bottom");
			}
		} catch (error) {
			addNotice('下書きの保存に失敗しました', "error", "bottom");
		} finally {
			setReplyLoading(prev => ({
				...prev,
				[review.id]: false
			}));
		}
	};

	// 返信送信
	const handleSendReply = async (review: Review) => {
		const replyText = replyTexts[review.id]?.trim();
		if (!replyText) return;

		setReplyLoading(prev => ({
			...prev,
			[review.id]: true
		}));

		try {
			const res = await nickFetch("/reviews/reply_to_review/", {
				review_id: review.id,
				reply_comment: replyText
			});

			if (!res.error) {
				// 返信成功時はレビューを再取得
				setSelectedReview( res.data.review as Review );
				await refleshReviews(currentPage);
				addNotice("返信を送信しました", "success", "bottom");
			} else {
				addNotice('返信の送信に失敗しました: ' + res.error, "error", "bottom");
			}
		} catch (error) {
			addNotice('返信の送信に失敗しました', "error", "bottom");
		} finally {
			setReplyLoading(prev => ({
				...prev,
				[review.id]: false
			}));
		}
	};

	// クチコミ自動生成
	const handleGenerateReply = async (review: Review) => {
		setGenerateLoading(prev => ({
			...prev,
			[review.id]: true
		}));

		try {
			const res = await nickFetch("/reviews/generate_reply/", {
				shop_id: shop.id,
				review_comment: review.review_comment,
				star_rating: Number(review.star_rating),
				reviewer_name: review.reviewer_name,
				custom_prompt: syncSettings.prompt,
			});
			if (!res.error && res.data?.reply_text) {
				// 生成された返信をテキストエリアに設定
				setReplyTexts(prev => ({
					...prev,
					[review.id]: res.data.reply_text
				}));
			} else {
				alert('返信内容の生成に失敗しました');
			}
		} catch (error) {
			alert('返信内容の生成に失敗しました');
		} finally {
			setGenerateLoading(prev => ({
				...prev,
				[review.id]: false
			}));
		}
	};

	// 日時をフォーマット
	const formatDate = (dateString: string) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleString('ja-JP', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		} catch {
			return dateString;
		}
	};

	// 評価の色を取得
	const getRatingColor = (rating: number | string) => {
		const numRating = Number(rating);
		if (numRating >= 4) return 'success';
		if (numRating >= 3) return 'warning';
		return 'error';
	};

	// 絞り込みと並び替えのロジック（サーバー側で処理するため、ここでは単純に表示のみ）
	const displayReviews = reviews || [];

	// 総ページ数はAPIから取得

	// 詳細表示ダイアログを開く
	const handleShowDetail = (review: Review) => {
		setReplyTexts(prev => ({
			...prev,
			[review.id]: review.reply_body
		}));
		setSelectedReview(review);
		setDetailDialogOpen(true);
	};

	// 詳細表示ダイアログを閉じる
	const handleCloseDetail = () => {
		setDetailDialogOpen(false);
		setSelectedReview(null);
	};

	// シミュレーターを開く
	const handleOpenSimulator = () => {
		setSimulatorOpen(true);
		// 現在の設定をシミュレーターに反映
		setSimulatorData(prev => ({
			...prev,
			prompt: syncSettings.prompt || ""
		}));
	};

	// シミュレーターを閉じる
	const handleCloseSimulator = () => {
		setSimulatorOpen(false);
		// 元の設定に戻す
		setSimulatorData(prev => ({
			...prev,
			prompt: syncSettings.prompt || ""
		}));
	};

	// プロンプトを適用してシミュレーターを閉じる
	const handleApplyPrompt = () => {
		setSimulatorOpen(false);
		// シミュレーターのプロンプトを設定に反映
		setSyncSettings(prev => ({
			...prev,
			prompt: simulatorData.prompt
		}));
		addNotice("プロンプトを設定に反映しました", "success", "bottom");
	};

	// シミュレーターで自動生成
	const handleSimulatorGenerate = async () => {
		setSimulatorLoading(true);
		try {
			const res = await nickFetch("/reviews/generate_reply/", {
				shop_id: shop.id,
				review_comment: simulatorData.sampleComment,
				star_rating: simulatorData.sampleRating,
				reviewer_name: simulatorData.sampleUserName,
				custom_prompt: simulatorData.prompt
			});
			if (!res.error && res.data?.reply_text) {
				setSimulatorData(prev => ({
					...prev,
					generatedReply: res.data.reply_text
				}));
			} else {
				alert('返信内容の生成に失敗しました');
			}
		} catch (error) {
			alert('返信内容の生成に失敗しました');
		} finally {
			setSimulatorLoading(false);
		}
	};

	// shop_sync設定の保存
	const handleSaveShopSync = async () => {
		if (!shop_sync) return;
		
		setSaving(true);
		try {
			const res = await nickFetch("/save_shop_sync/", {
				shop_id: shop.id,
				sync: {
					source_platform: shop_sync.source_platform,
					enabled: syncSettings.enabled,
					post_status: syncSettings.post_status,
					bad_review_star: syncSettings.bad_review_star,
					bad_review_notify: syncSettings.bad_review_notify,
					rate_only_reply: syncSettings.rate_only_reply,
					start_date: syncSettings.start_date,
					prompt: syncSettings.prompt
				}
			});
			
			if (!res.error) {
				addNotice("設定を保存しました", "success", "bottom");
				// 保存後にデータを再取得
				await refleshReviews();
			} else {
				addNotice("設定の保存に失敗しました", "error", "bottom");
			}
		} catch (error) {
			addNotice("設定の保存に失敗しました", "error", "bottom");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div>
			<Box pb={3}>
				<Typography mt={2} mb={3} sx={{ fontSize: "1.2rem", fontWeight: "bold" }}>AIクチコミ自動返信（β版）</Typography>
			</Box>

			<Box mb={4}>
				<Card sx={{ position: "relative" }}>
					<CardContent>
						<Grid container sx={{ alignItems: 'center' }}>
							<Grid item xs={12} md={6}>
								<Box>
									<FormControl>
										<FormLabel>ステータス</FormLabel>
										{/* 内部的にクチコミの取り込みだけしてupdate_dt入れてない場合がある. 初回だけ. */}
										{shop_sync.update_dt ? <SyncStatusBar sync={shop_sync} /> : <>
											{shop_sync.status == SyncStatus.ERROR ? (
												<>
													<Typography>エラー</Typography>
													<Typography sx={{ fontSize: ".8rem",color:"red" }}>{shop_sync.log_line}</Typography>
												</>
											) : <Typography>処理待ち</Typography>}
										</>}
										
										{shop_sync.enabled && !shop_sync.update_dt && 0 < displayReviews.length && (
											<Typography mt={1} variant="body1" color="error">
												自動生成処理が完了するまでしばらくお待ち下さい
											</Typography>
										)}
									</FormControl>
								</Box>
							</Grid>
							<Grid item xs={12} md={6}>
								<Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
									<Link onClick={() => setShowSettings(v => !v)} sx={{ cursor: 'pointer' }}>
										{showSettings ? '設定を隠す' : '設定を表示'}
									</Link>
								</Box>
							</Grid>
						</Grid>
						{showSettings && (
							<Grid container sx={{ borderTop: "1px solid #ccc", mt:2, pt:2 }} id="js-review-settings">
								<Grid item xs={12} md={6}>
									<Box sx={{ py: 1 }}>
										<FormControl>
											<FormLabel>取り込み</FormLabel>
											<RadioGroup 
												row
												value={syncSettings.enabled}
												onChange={(e) => setSyncSettings(prev => ({
													...prev,
													enabled: Number(e.target.value)
												}))}
											>
												<FormControlLabel 
													control={<Radio value="0" />} 
													label="無効" 
												/>
												<FormControlLabel 
													control={<Radio value="1" />} 
													label="有効" 
												/>
											</RadioGroup>
										</FormControl>
									</Box>
									<Box sx={{ py: 1 }}>
										<FormControl>
											<FormLabel>投稿設定</FormLabel>
											<RadioGroup 
												row
												value={syncSettings.post_status.toString()}
												onChange={(e) => setSyncSettings(prev => ({
													...prev,
													post_status: parseInt(e.target.value)
												}))}
											>
												<FormControlLabel 
													control={<Radio value="50" />} 
													label="生成しない" 
												/>
												<FormControlLabel 
													control={<Radio value="51" />} 
													label="生成のみ" 
												/>
												<FormControlLabel 
													control={<Radio value="52" />} 
													label="生成＆自動投稿" 
												/>
											</RadioGroup>
										</FormControl>
									</Box>
									<Box sx={{ py: 1 }}>
										<FormControl>
											<FormLabel sx={{ minWidth: 'fit-content' }}>悪い口コミ</FormLabel>
											<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
												<Select 
													size="small" 
													sx={{ minWidth: 120 }}
													value={syncSettings.bad_review_star}
													onChange={(e) => setSyncSettings(prev => ({
														...prev,
														bad_review_star: parseInt(e.target.value as string)
													}))}
												>
													<MenuItem value={0}>☆0</MenuItem>
													<MenuItem value={1}>☆1</MenuItem>
													<MenuItem value={2}>☆2</MenuItem>
													<MenuItem value={3}>☆3</MenuItem>
													<MenuItem value={4}>☆4</MenuItem>
													<MenuItem value={5}>☆5</MenuItem>
												</Select>
												<Typography>以下の口コミに自動返信しない</Typography>
											</Box>
											<Box mt={1}>
												<FormControlLabel
													control={
														<Checkbox
															checked={syncSettings.bad_review_notify === 1}
															onChange={(e) => setSyncSettings(prev => ({
																...prev,
																bad_review_notify: e.target.checked ? 1 : 0
															}))}
														/>
													}
													label="悪い口コミが入ったらメール通知する"
												/>
											</Box>
										</FormControl>
									</Box>
									<Box sx={{ py: 1 }}>
										<FormControl>
											<FormLabel>評価のみのクチコミ</FormLabel>
											<RadioGroup 
												row
												value={syncSettings.rate_only_reply.toString()}
												onChange={(e) => setSyncSettings(prev => ({
													...prev,
													rate_only_reply: parseInt(e.target.value)
												}))}
											>
												<FormControlLabel 
													control={<Radio value="0" />} 
													label="返信しない" 
												/>
												<FormControlLabel 
													control={<Radio value="1" />} 
													label="返信する" 
												/>
											</RadioGroup>
										</FormControl>
									</Box>
									<Box sx={{ py: 1 }}>
										<FormControl>
											<FormLabel>返信開始日</FormLabel>
											<TextField
												type="date"
												variant="outlined"
												size="small"
												sx={{ width: 300 }}
												value={syncSettings.start_date}
												onChange={(e) => setSyncSettings(prev => ({
													...prev,
													start_date: e.target.value
												}))}
												helperText="この日付より前のレビューには返信しません"
											/>
										</FormControl>
									</Box>
								</Grid>
								<Grid item xs={12} md={6}>
									<Box sx={{ py: 1 }}>
										<FormControl fullWidth>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
												<FormLabel>プロンプト</FormLabel>
												<Button variant="outlined" size="small" onClick={handleOpenSimulator}>返信シミュレーター</Button>
											</Box>
											<TextField
												multiline
												rows={8}
												placeholder="返信生成用のプロンプトを入力してください..."
												variant="outlined"
												size="small"
												fullWidth
												value={syncSettings.prompt}
												onChange={(e) => setSyncSettings(prev => ({
													...prev,
													prompt: e.target.value
												}))}
											/>
										</FormControl>
									</Box>
									<Box sx={{ py: 1 }}>
										<LoadingButton
											variant="outlined"
											onClick={handleSaveShopSync}
											loading={saving}
											disabled={!shop_sync}
										>保存</LoadingButton>
									</Box>
								</Grid>
							</Grid>
						)}
					</CardContent>
				</Card>
			</Box>

			{/* 統計情報 */}
			{/* 
			<Box mb={4}>
				<Card>
					<CardContent>
						<Grid container spacing={3}>
							<Grid item xs={12} md={4}>
								<Box textAlign="center">
									<Typography variant="h4" color="primary" fontWeight="bold">
										{reviews?.averageRating?.toFixed(1) || '-'}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										平均評価
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12} md={4}>
								<Box textAlign="center">
									<Typography variant="h4" color="primary" fontWeight="bold">
										{reviews?.totalReviewCount?.toLocaleString() || '-'}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										総クチコミ数
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={12} md={4}>
								<Box textAlign="center">
									<Typography variant="h4" color="primary" fontWeight="bold">
										{reviews?.reviews?.length || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										表示中のクチコミ数
									</Typography>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Box>
			 */}

			{/* 絞り込み・並び替え */}
			<Box mb={3}>
				<Card>
					<CardContent>
						<Typography variant="h6" mb={2}>絞り込み・並び替え</Typography>
						<Grid container spacing={2}>
							{/* 返信ステータス */}
							<Grid item xs={12} md={3}>
								<FormControl fullWidth size="small">
									<FormLabel>投稿ステータス</FormLabel>
									<Select
										value={filterStatus}
										onChange={(e) => setFilterStatus(e.target.value)}
									>
										<MenuItem value="all">すべて</MenuItem>
										<MenuItem value="0">処理待ち</MenuItem>
										<MenuItem value="5">生成済み</MenuItem>
										<MenuItem value="1">下書き</MenuItem>
										<MenuItem value="14">投稿済み</MenuItem>
										<MenuItem value="16">投稿エラー</MenuItem>
										<MenuItem value="99">無視</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							
							{/* 評価 */}
							<Grid item xs={12} md={3}>
								<FormControl fullWidth size="small">
									<FormLabel>評価</FormLabel>
									<Select
										value={filterRating}
										onChange={(e) => setFilterRating(e.target.value)}
									>
										<MenuItem value="all">すべて</MenuItem>
										<MenuItem value="1">☆1</MenuItem>
										<MenuItem value="2">☆2</MenuItem>
										<MenuItem value="3">☆3</MenuItem>
										<MenuItem value="4">☆4</MenuItem>
										<MenuItem value="5">☆5</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							
							{/* コメントの有無 */}
							<Grid item xs={12} md={3}>
								<FormControl fullWidth size="small">
									<FormLabel>コメント</FormLabel>
									<Select
										value={filterHasComment}
										onChange={(e) => setFilterHasComment(e.target.value)}
									>
										<MenuItem value="all">すべて</MenuItem>
										<MenuItem value="has">コメントあり</MenuItem>
										<MenuItem value="none">コメントなし</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							
							{/* 並び替え */}
							<Grid item xs={12} md={3}>
								<FormControl fullWidth size="small">
									<FormLabel>並び替え</FormLabel>
									<Select
										value={`${sortBy}-${sortOrder}`}
										onChange={(e) => {
											const [newSortBy, newSortOrder] = e.target.value.split('-');
											setSortBy(newSortBy);
											setSortOrder(newSortOrder);
										}}
									>
										<MenuItem value="create_dt-desc">投稿日時（新しい順）</MenuItem>
										<MenuItem value="create_dt-asc">投稿日時（古い順）</MenuItem>
										<MenuItem value="star_rating-desc">評価（高い順）</MenuItem>
										<MenuItem value="star_rating-asc">評価（低い順）</MenuItem>
									</Select>
								</FormControl>
							</Grid>
						</Grid>
						
						{/* 絞り込み結果 */}
						<Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
							<Typography variant="body2" color="text.secondary">
								表示中: {displayReviews.length}件 / 全{totalCount}件
							</Typography>
							{(filterStatus !== 'all' || filterRating !== 'all' || filterHasComment !== 'all') && (
								<Button
									variant="outlined"
									size="small"
									onClick={() => {
										setFilterStatus('all');
										setFilterRating('all');
										setFilterHasComment('all');
									}}
								>
									絞り込みをクリア
								</Button>
							)}
						</Box>
					</CardContent>
				</Card>
			</Box>

			{/* クチコミ一覧 */}
			<Box>
				<Typography variant="h6" mb={2}>クチコミ一覧</Typography>
				
				{loading ? (
					<Box textAlign="center" py={4}>
						<Typography>読み込み中...</Typography>
					</Box>
				) : displayReviews && displayReviews.length > 0 ? (
					<Grid container spacing={2}>
						{displayReviews.map((review, index) => (
							<Grid item xs={12} md={4} key={review.id || index}>
								<Card>
									<CardContent>
										{/* クチコミヘッダー */}
										<Box display="flex" alignItems="center" mb={2}>
											<Avatar 
												src={review.reviewer_photo_url}
												sx={{ mr: 2 }}
											>
												{review.reviewer_name.charAt(0)}
											</Avatar>
											<Box flex={1}>
												<Typography variant="subtitle1" fontWeight="bold">
													{review.reviewer_name}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{formatDate(review.create_dt)}
												</Typography>
											</Box>
											{/* <Chip 
												label={getStatusLabel(review.post_status)}
												color={getStatusColor(review.post_status) as any}
												variant="outlined"
											/> */}
										</Box>

										{/* 評価 */}
										<Box mb={2}>
											<Rating 
												value={Number(review.star_rating)} 
												readOnly 
												size="small"
												sx={{ mr: 1 }}
											/>
											<Typography variant="body2" color="text.secondary" component="span">
												{review.star_rating}/5
											</Typography>
										</Box>

										{/* コメント（省略表示） */}
										<Typography variant="body2" mb={2} sx={{ 
											whiteSpace: 'nowrap', 
											overflow: 'hidden', 
											textOverflow: 'ellipsis',
											maxHeight: '10.2em',
											color: review.review_comment ? 'text.primary' : 'text.disabled',
										}}>
											{review.review_comment || 'コメントなし'}
										</Typography>

										{/* オリジナルデータの返信内容（省略表示） */}
										{getOriginalReplyComment(review) && (
											<Box mt={1} bgcolor="grey.50" borderRadius={1}>
												<Typography variant="caption" fontWeight="bold" color="">
													店舗からの返信（投稿済み）
												</Typography>
												<Typography variant="caption" sx={{ 
													whiteSpace: 'nowrap', 
													overflow: 'hidden', 
													textOverflow: 'ellipsis',
													display: 'block',
													maxHeight: '1.2em',
												}}>
													{getOriginalReplyComment(review)}
												</Typography>
											</Box>
										)}

										{/* 投稿ステータス */}
										{shop_sync && (
											<Box mt={1}>
												<Chip 
													label={getStatusLabel(review.post_status)}
													color={getStatusColor(review.post_status) as any}
													variant="outlined"
													size="small"
												/>
											</Box>
										)}
										{/* エラー内容 */}
										{shop_sync && review.post_status == PostStatus.POSTING_ERROR && (
											<Box mt={1}>
												<Typography variant="caption" sx={{ 
													whiteSpace: 'nowrap', 
													overflow: 'hidden', 
													textOverflow: 'ellipsis',
													display: 'block',
													maxHeight: '1.2em',
													color: 'error.main',
												}}>
													{review.error_message}
												</Typography>
											</Box>
										)}

										{/* 返信（省略表示） */}
										{review.reply_body && review.reply_body != getOriginalReplyComment(review) && (
											<Box mt={1} bgcolor="grey.50" borderRadius={1}>
												<Typography variant="caption" fontWeight="bold" color="primary">
													店舗からの返信
												</Typography>
												<Typography variant="caption" sx={{ 
													whiteSpace: 'nowrap', 
													overflow: 'hidden', 
													textOverflow: 'ellipsis',
													display: 'block',
													maxHeight: '1.2em',
												}}>
													{review.reply_body}
												</Typography>
											</Box>
										)}

										{/* 詳細表示ボタン */}
										<Box mt={2} display="flex" justifyContent="center">
											<Button
												variant="outlined"
												size="small"
												onClick={() => handleShowDetail(review)}
											>
												詳細を表示
											</Button>
										</Box>
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>
				) : (
					<Box textAlign="center" py={4}>
						<Typography color="text.secondary">
							クチコミがありません
						</Typography>
					</Box>
				)}
			</Box>

			{/* ページャー */}
			{displayReviews && displayReviews.length > 0 && totalPages > 1 && (
				<Box mt={4} display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2}>
					<Typography variant="body2" color="text.secondary" mb={1}>
						表示中: {displayReviews.length}件 / 全{totalCount}件
					</Typography>
					<Pagination 
						count={totalPages} 
						page={currentPage} 
						onChange={handlePageChange}
						color="primary"
					/>
				</Box>
			)}

			{/* 詳細表示ダイアログ */}
			<Dialog 
				open={detailDialogOpen} 
				onClose={handleCloseDetail}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					{selectedReview && (
						<Box display="flex" alignItems="center">
							<Avatar 
								sx={{ mr: 2 }}
							>
								{selectedReview.reviewer_name.charAt(0)}
							</Avatar>
							<Box flex={1}>
								<Typography variant="h6">
									{selectedReview.reviewer_name}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{formatDate(selectedReview.create_dt)}
								</Typography>
							</Box>
							<Box>
								{/* 返信ステータス */}
								{selectedReview && (
									<Chip 
										label={getStatusLabel(selectedReview.post_status)}
										color={getStatusColor(selectedReview.post_status) as any}
										variant="outlined"
										size="small"
									/>
								)}
							</Box>
						</Box>
					)}
				</DialogTitle>
				<DialogContent>
					{selectedReview && (
						<Box>
							{/* 評価 */}
							<Box mb={2}>
								<Rating 
									value={Number(selectedReview.star_rating)} 
									readOnly 
									size="large"
									sx={{ mr: 1 }}
								/>
								<Typography variant="body1" color="text.secondary" component="span">
									{selectedReview.star_rating}/5
								</Typography>
							</Box>

							{/* エラー内容 */}
							{selectedReview && selectedReview.post_status == PostStatus.POSTING_ERROR && (
								<Box mt={1} mb={2}>
									<Typography variant="caption" sx={{ 
										display: 'block',
										color: 'error.main',
										whiteSpace: 'pre-wrap', 
									}}>
										{selectedReview.error_message}
									</Typography>
								</Box>
							)}
							
							{/* クチコミ内容 */}
							<Typography variant="caption" fontWeight="bold">クチコミ内容</Typography>
							<Typography variant="body1" mb={3} sx={{ whiteSpace: 'pre-wrap' }} color={selectedReview.review_comment ? 'text.primary' : 'text.disabled'}>
								{selectedReview.review_comment || 'コメントなし'}
							</Typography>

							{/* オリジナルデータの返信内容（省略表示） */}
							{getOriginalReplyComment(selectedReview) && (
								<Box mt={1} bgcolor="grey.50" borderRadius={1}>
									<Typography variant="caption" fontWeight="bold">
										店舗からの返信（投稿済み）
									</Typography>
									<Typography sx={{ 
										whiteSpace: 'pre-wrap', 
										display: 'block',
									}}>
										{getOriginalReplyComment(selectedReview)}
									</Typography>
								</Box>
							)}

							{/* 返信フォーム */}
							{shop_sync && (
								<Box mt={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
									<Typography variant="h6" mb={1} color="primary">
										返信を編集
									</Typography>
									<TextareaAutosize
										minRows={4}
										placeholder="返信を入力してください..."
										value={replyTexts[selectedReview.id]}
										onChange={(e) => handleReplyTextChange(selectedReview.id, e.target.value)}
										disabled={replyLoading[selectedReview.id]}
										style={{
											width: '100%',
											padding: '12px',
											border: '1px solid #ccc',
											borderRadius: '4px',
											fontFamily: 'inherit',
											fontSize: '14px',
											resize: 'vertical'
										}}
									/>
									<Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
										<Button
											variant="outlined"
											onClick={() => handleGenerateReply(selectedReview)}
											disabled={generateLoading[selectedReview.id]}
											startIcon={<span>🤖</span>}
										>
											{generateLoading[selectedReview.id] ? '生成中...' : '自動生成実行'}
										</Button>
										<Box display="flex" gap={1}>
											<Button
												variant="outlined"
												onClick={() => handleSaveDraft(selectedReview)}
												disabled={replyLoading[selectedReview.id] || replyTexts[selectedReview.id] == selectedReview.reply_body}
											>
												{replyLoading[selectedReview.id] ? '保存中...' : '下書き保存'}
											</Button>
											<Button
												variant="contained"
												onClick={() => handleSendReply(selectedReview)}
												disabled={replyLoading[selectedReview.id] || !replyTexts[selectedReview.id]?.trim()}
											>
												{replyLoading[selectedReview.id] ? '送信中...' : '返信送信'}
											</Button>
										</Box>
									</Box>
								</Box>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDetail}>閉じる</Button>
				</DialogActions>
			</Dialog>

			{/* 返信シミュレーターダイアログ */}
			<Dialog 
				open={simulatorOpen} 
				onClose={handleCloseSimulator}
				maxWidth="lg"
				fullWidth
			>
				<DialogTitle>
					<Typography variant="h6">返信シミュレーター</Typography>
					<Typography variant="body2" color="text.secondary">
						サンプルコメントとプロンプトを編集して、自動生成結果を確認できます
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={3} sx={{ mt: 1 }}>
						<Grid item xs={12} md={6}>
							<Box>
								<Typography variant="h6" mb={2}>サンプルコメント</Typography>
								<Box mb={2}>
									{/* <Typography variant="body2" color="text.secondary" mb={1}>
										テンプレートから選択:
									</Typography> */}
									<Box display="flex" flexWrap="wrap" gap={1}>
										{sampleCommentTemplates.map((template, index) => (
											<Chip
												key={index}
												label={template.label}
												variant="outlined"
												size="small"
												onClick={() => setSimulatorData(prev => ({
													...prev,
													sampleComment: template.comment,
													sampleRating: template.rating,
													sampleUserName: template.userName
												}))}
												sx={{ cursor: 'pointer' }}
											/>
										))}
									</Box>
								</Box>
								<Box mb={2}>
									<Typography variant="body2" color="text.secondary" mb={1}>
										評価:
									</Typography>
									<Rating
										value={simulatorData.sampleRating}
										onChange={(event, newValue) => {
											if (newValue !== null) {
												setSimulatorData(prev => ({
													...prev,
													sampleRating: newValue
												}));
											}
										}}
										size="large"
									/>
								</Box>
								<Box mb={2}>
									<Typography variant="body2" color="text.secondary" mb={1}>
										ユーザー名:
									</Typography>
									<TextField
										value={simulatorData.sampleUserName}
										onChange={(e) => setSimulatorData(prev => ({
											...prev,
											sampleUserName: e.target.value
										}))}
										placeholder="ユーザー名を入力してください"
										variant="outlined"
										size="small"
										fullWidth
									/>
								</Box>
								<TextField
									multiline
									rows={6}
									value={simulatorData.sampleComment}
									onChange={(e) => setSimulatorData(prev => ({
										...prev,
										sampleComment: e.target.value
									}))}
									placeholder="サンプルのクチコミコメントを入力してください..."
									variant="outlined"
									fullWidth
								/>
							</Box>
						</Grid>
						<Grid item xs={12} md={6}>
							<Box>
								<Typography variant="h6" mb={2}>プロンプト</Typography>
								<TextField
									multiline
									rows={12}
									value={simulatorData.prompt}
									onChange={(e) => setSimulatorData(prev => ({
										...prev,
										prompt: e.target.value
									}))}
									placeholder="返信生成用のプロンプトを入力してください..."
									variant="outlined"
									fullWidth
								/>
							</Box>
						</Grid>
						<Grid item xs={12}>
							<Box display="flex" justifyContent="center" mt={2}>
								<LoadingButton
									variant="contained"
									onClick={handleSimulatorGenerate}
									loading={simulatorLoading}
									startIcon={<span>🤖</span>}
									size="large"
								>
									自動生成実行
								</LoadingButton>
							</Box>
						</Grid>
						{simulatorData.generatedReply && (
							<Grid item xs={12}>
								<Box mt={3}>
									<Typography variant="h6">生成結果</Typography>
									<Box p={3} bgcolor="grey.50" borderRadius={2} border={1} borderColor="grey.300">
										<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
											{simulatorData.generatedReply}
										</Typography>
									</Box>
								</Box>
							</Grid>
						)}
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseSimulator}>閉じる</Button>
					<Button 
						variant="contained" 
						onClick={handleApplyPrompt}
						color="primary"
					>
						このプロンプトを使用する
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
}

